/**
 * Shared state for PDP bundle builder.
 * Selections keyed by variantId. Each flavor maps to one metafield variant;
 * units become cart quantity of that same variant (e.g. 3× → qty 3 of the 20-pack).
 */
window.BundleBuilder = window.BundleBuilder || {
  items: {},

  /**
   * @returns {Array<{id: number, quantity: number, productId: number, title: string, variantTitle: string, packs: number, flavor?: string, units?: number}>}
   */
  getItems() {
    return Object.values(this.items).filter((item) => item.quantity > 0);
  },

  /**
   * Cart /add.js payload lines.
   * @returns {Array<{id: number, quantity: number}>}
   */
  getCartLines() {
    return this.getItems().map(({ id, quantity }) => ({ id, quantity }));
  },

  /**
   * Payload ready for Shopify Cart API (/cart/add.js).
   * Does not include the PDP product — only builder selections.
   * Prefers each item's sellingPlanId (subscription). Falls back to form selling_plan.
   * @param {{ form?: HTMLFormElement }} [options]
   * @returns {{ items: Array<Record<string, unknown>> }}
   */
  getCartPayload(options = {}) {
    const form = options.form;
    const formSellingPlan = form?.querySelector('[name="selling_plan"]')?.value;
    const properties = {};

    form?.querySelectorAll('[name^="properties["]').forEach((input) => {
      const match = input.name.match(/^properties\[([^\]]+)\]$/);
      if (match && input.value !== '') {
        properties[match[1]] = input.value;
      }
    });

    const items = this.getItems().map((entry) => {
      const item = { id: Number(entry.id), quantity: Number(entry.quantity) || 1 };
      const sellingPlanId = entry.sellingPlanId || (formSellingPlan ? Number(formSellingPlan) : null);

      if (sellingPlanId) {
        item.selling_plan = Number(sellingPlanId);
      }

      if (Object.keys(properties).length) {
        item.properties = { ...properties };
      }

      return item;
    });

    return { items };
  },

  isBuilderPage() {
    return Boolean(document.querySelector('bundle-products'));
  },

  getTotalQuantity() {
    return this.getItems().reduce((sum, item) => sum + item.quantity, 0);
  },

  /**
   * Stick packs (servings) for one line item.
   * @param {{ packs?: number, units?: number }} item
   * @returns {number}
   */
  getItemServingCount(item) {
    const packs = Number(item.packs);
    const units = Number(item.units) || 0;

    if (Number.isFinite(packs) && packs > 1) return packs;
    if (units > 0) return units * 20;

    return 1;
  },

  getTotalPacks() {
    return this.getItems().reduce(
      (sum, item) => sum + (Number(item.quantity) || 1) * this.getItemServingCount(item),
      0
    );
  },

  /**
   * Automatic first-order discount rate from theme setting (e.g. 0.19).
   * @returns {number}
   */
  getAutomaticDiscountRate() {
    const raw = document.body?.dataset?.automaticDiscountValue;
    const rate = Number(raw);
    return Number.isFinite(rate) && rate > 0 && rate < 1 ? rate : 0;
  },

  /**
   * Summed prices for selected items (cents), with automatic discount applied
   * the same way as cart: sale = subtotal - floor(subtotal * rate).
   * @returns {{ priceCents: number, compareAtCents: number, subtotalCents: number, totalPacks: number, perServingCents: number, perServingCompareCents: number, hasItems: boolean }}
   */
  getTotals() {
    const items = this.getItems();
    const subtotalCents = items.reduce(
      (sum, item) => sum + (Number(item.priceCents) || 0) * (Number(item.quantity) || 1),
      0
    );
    const compareBaseCents = items.reduce(
      (sum, item) =>
        sum + (Number(item.compareAtCents) || Number(item.priceCents) || 0) * (Number(item.quantity) || 1),
      0
    );

    const discountRate = this.getAutomaticDiscountRate();
    let priceCents = subtotalCents;
    let compareAtCents = Math.max(compareBaseCents, subtotalCents);

    if (discountRate > 0 && subtotalCents > 0) {
      const discountAmount = Math.floor(subtotalCents * discountRate);
      priceCents = subtotalCents - discountAmount;
      // Strikethrough = price before automatic discount (matches cart original_price)
      compareAtCents = Math.max(compareAtCents, subtotalCents);
    }

    const totalPacks = this.getTotalPacks();
    const perServingCents = totalPacks > 0 ? Math.ceil(priceCents / totalPacks) : 0;
    const perServingCompareCents = totalPacks > 0 ? Math.ceil(compareAtCents / totalPacks) : 0;

    return {
      priceCents,
      compareAtCents,
      subtotalCents,
      totalPacks,
      perServingCents,
      perServingCompareCents,
      hasItems: items.length > 0,
    };
  },

  getFlavorUnits(flavor) {
    const item = this.getItems().find((entry) => entry.flavor === flavor);
    return item?.units || 0;
  },

  /**
   * Set units for a flavor. Same metafield variant, cart quantity = units.
   * @param {string} flavor
   * @param {number} units 0 = remove flavor; 3 = add qty 3 of that variant
   * @param {{ id: number, productId: number, title?: string, variantTitle?: string, packs?: number } | null} variantItem
   */
  setFlavorUnits(flavor, units, variantItem) {
    const nextUnits = Math.max(0, Number(units) || 0);

    Object.keys(this.items).forEach((key) => {
      if (this.items[key].flavor === flavor) {
        delete this.items[key];
      }
    });

    if (nextUnits > 0 && variantItem?.id) {
      this.items[variantItem.id] = {
        id: Number(variantItem.id),
        productId: Number(variantItem.productId),
        title: variantItem.title || '',
        variantTitle: variantItem.variantTitle || '',
        packs: Number(variantItem.packs) || 20,
        quantity: nextUnits,
        flavor,
        units: nextUnits,
        sellingPlanId: variantItem.sellingPlanId ? Number(variantItem.sellingPlanId) : null,
        priceCents: Number(variantItem.priceCents) || 0,
        compareAtCents: Number(variantItem.compareAtCents) || Number(variantItem.priceCents) || 0,
      };
    }

    this.notify();
    return this.getItems();
  },

  /**
   * @param {{ id: number, productId: number, title?: string, variantTitle?: string, packs?: number, flavor?: string, units?: number }} item
   * @param {number} quantity
   */
  setItem(item, quantity) {
    const id = Number(item.id);
    const qty = Math.max(0, Number(quantity) || 0);

    if (!id) return this.getItems();

    if (qty <= 0) {
      delete this.items[id];
    } else {
      this.items[id] = {
        id,
        productId: Number(item.productId),
        title: item.title || '',
        variantTitle: item.variantTitle || '',
        packs: Number(item.packs) || 1,
        quantity: qty,
        flavor: item.flavor || '',
        units: item.units || qty,
      };
    }

    this.notify();
    return this.getItems();
  },

  increment(item, by = 1) {
    const current = this.items[item.id]?.quantity || 0;
    return this.setItem(item, current + by);
  },

  decrement(item, by = 1) {
    const current = this.items[item.id]?.quantity || 0;
    return this.setItem(item, current - by);
  },

  getQuantity(variantId) {
    return this.items[variantId]?.quantity || 0;
  },

  clear() {
    this.items = {};
    this.notify();
    return this.getItems();
  },

  notify() {
    const cart = this.getCartPayload();
    const totals = this.getTotals();
    const detail = {
      cart,
      items: this.getItems(),
      totals,
      totalQuantity: this.getTotalQuantity(),
      totalPacks: totals.totalPacks,
    };

    // Object that will be sent to /cart/add.js
    console.log('[BundleBuilder] cart payload', cart);
    console.log(
      '[BundleBuilder] cart items detail',
      this.getItems().map((item) => ({
        id: item.id,
        quantity: item.quantity,
        selling_plan: item.sellingPlanId || null,
        title: item.title,
        flavor: item.flavor,
        variantTitle: item.variantTitle,
        packs: item.packs,
        units: item.units,
        priceCents: item.priceCents,
        compareAtCents: item.compareAtCents,
      }))
    );

    document.dispatchEvent(
      new CustomEvent('bundle-builder:change', {
        bubbles: true,
        detail,
      })
    );

    const progress = document.querySelector('bundle-progress');
    if (progress && typeof progress.setQuantity === 'function') {
      progress.setQuantity(detail.totalPacks);
    }
  },
};
